import React, { useState, useCallback, useEffect } from 'react';
import {
  ManualOrderCreateData,
  ManualOrderLineItem,
  CustomerInfo,
  PaymentMethod,
  Product,
  CouponDiscountType,
  ColorPricingSettings
} from '../../../types';
import { orderService } from '../../../services/orderService';
import { getColorPricingSettings, getProducts } from '../../../services/firebase';
import { FormField } from '../atoms';
import { CAKE_COLORS, getBaseColorAdditionalPrice } from '../../../constants/cakeColors';
import './CreateOrderModal.css';

const PAYMENT_METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' }
];

const CUSTOM_LINE_ITEM_VALUE = '__custom__';

type CreateOrderLineDraft = ManualOrderLineItem & {
  productId?: string;
  unitPriceText?: string;
};

const isCustomLineItem = (line: { productId?: string }): boolean =>
  line.productId === CUSTOM_LINE_ITEM_VALUE;

const emptyLineItem = (): CreateOrderLineDraft => ({
  productId: '',
  name: '',
  quantity: 1,
  unitPrice: 0,
  size: '',
  shape: '',
  layers: '',
  flavors: [],
  filling: '',
  icing: '',
  baseColor: '',
  pipingColor: '',
  specialInstructions: '',
  addOns: [],
  matchingCupcakes: [],
  description: ''
});

const AVAILABLE_COLORS = CAKE_COLORS;

const parsePlusEuroPrice = (s: string | undefined): number => {
  if (!s) return 0;
  if (!s.includes('+€')) return 0;
  const priceMatch = s.match(/\+€(\d+(?:\.\d+)?)/);
  return priceMatch ? parseFloat(priceMatch[1]) : 0;
};

const computeUnitPrice = (
  product: Product,
  line: CreateOrderLineDraft,
  colorPricingSettings?: ColorPricingSettings | null
): number => {
  // Base price: comes from selected size (and optionally quantity tiers).
  const selectedSize =
    (product.sizes || []).find((s) => s.name === line.size) || (product.sizes || [])[0];

  let basePricePerUnit = selectedSize?.price ?? 0;

  if (selectedSize?.quantityTiers && selectedSize.quantityTiers.length > 0) {
    const currentTier =
      selectedSize.quantityTiers.find((t) => t.quantity === line.quantity) ||
      selectedSize.quantityTiers[0];
    // For quantity tiers, mirror the same "price per unit" logic as ProductDetailPage.
    basePricePerUnit = currentTier.price / currentTier.quantity;
  }

  // Additional costs are treated as per-unit amounts in the customer flow.
  const flavorAdditionalPrice = (line.flavors || []).reduce((total, flavorName) => {
    const flavor = (product.flavors || []).find((f) => f.name === flavorName);
    return total + (flavor?.price ?? 0);
  }, 0);

  const fillingAdditionalPrice = parsePlusEuroPrice(line.filling);
  const icingAdditionalPrice = parsePlusEuroPrice(line.icing);

  const addOnsAdditionalPrice = (line.addOns || []).reduce(
    (total, addOn) => total + parsePlusEuroPrice(addOn),
    0
  );

  const matchingCupcakesAdditionalPrice = (line.matchingCupcakes || []).reduce(
    (total, cupcake) => total + parsePlusEuroPrice(cupcake),
    0
  );

  const baseColorAdditionalPrice =
    product.enableBaseColor !== false
      ? getBaseColorAdditionalPrice(line.baseColor, {
          collection: product.collection,
          productId: product.id,
          settings: colorPricingSettings
        })
      : 0;

  const unitPrice = basePricePerUnit + flavorAdditionalPrice + fillingAdditionalPrice + icingAdditionalPrice + addOnsAdditionalPrice + matchingCupcakesAdditionalPrice + baseColorAdditionalPrice;
  return Math.round(unitPrice * 100) / 100;
};

const emptyCustomer = (): CustomerInfo => ({
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Ireland'
  },
  deliveryOption: 'collection'
});

interface CreateOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editMode?: boolean;
  existingOrderId?: string;
  existingOrderData?: {
    customer: CustomerInfo;
    items: CreateOrderLineDraft[];
    deliveryOption: 'collection' | 'delivery';
    deliveryDate?: Date;
    deliveryInstructions?: string;
    paymentMethod: PaymentMethod;
    paymentId?: string;
    shippingAmount?: number;
    adminDiscountType?: CouponDiscountType | '';
    adminDiscountValue?: number;
    adminNotes?: string;
  };
}

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editMode = false,
  existingOrderId,
  existingOrderData
}) => {
  const [customer, setCustomer] = useState<CustomerInfo>(emptyCustomer);
  const [items, setItems] = useState<CreateOrderLineDraft[]>([emptyLineItem()]);
  const [deliveryOption, setDeliveryOption] = useState<'collection' | 'delivery'>('collection');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [paymentId, setPaymentId] = useState('');
  const [shippingAmount, setShippingAmount] = useState<string>('');
  const [discountType, setDiscountType] = useState<CouponDiscountType | ''>('');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [colorPricingSettings, setColorPricingSettings] = useState<ColorPricingSettings | null>(null);

  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set([0]));

  const toggleItemExpanded = useCallback((index: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const updateCustomer = useCallback(
    (field: keyof CustomerInfo, value: string | CustomerInfo['address']) => {
      setCustomer((prev) =>
        field === 'address'
          ? { ...prev, address: value as CustomerInfo['address'] }
          : { ...prev, [field]: value }
      );
    },
    []
  );

  const updateAddress = useCallback((field: string, value: string) => {
    setCustomer((prev) => ({
      ...prev,
      address: { ...(prev.address || emptyCustomer().address!), [field]: value }
    }));
  }, []);

  const setItem = useCallback((index: number, updates: Partial<CreateOrderLineDraft>) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  }, []);

  const addLineItem = useCallback(() => {
    setItems((prev) => {
      setExpandedItems((e) => new Set(e).add(prev.length));
      return [...prev, emptyLineItem()];
    });
  }, []);

  const removeLineItem = useCallback((index: number) => {
    setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
    setExpandedItems((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
  }, []);

  const subtotal = items.reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0),
    0
  );

  const resetForm = useCallback(() => {
    setCustomer(emptyCustomer());
    setItems([emptyLineItem()]);
    setDeliveryOption('collection');
    setDeliveryDate('');
    setDeliveryInstructions('');
    setPaymentMethod('cash');
    setPaymentId('');
    setShippingAmount('');
    setDiscountType('');
    setDiscountValue('');
    setAdminNotes('');
    setError(null);
    setProducts([]);
    setProductsLoading(false);
    setProductsError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  // Load products when modal opens (for product-driven line items)
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        setProductsError(null);

        const [data, colorSettings] = await Promise.all([
          getProducts(),
          getColorPricingSettings()
        ]);
        if (cancelled) return;

        // Prefer only active products for selection
        setProducts((data || []).filter((p) => p.isActive));
        setColorPricingSettings(colorSettings);
      } catch (err) {
        console.error('Failed to load products:', err);
        if (!cancelled) setProductsError(err instanceof Error ? err.message : 'Failed to load products');
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    };

    fetchProducts();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Initialize form with existing order data when in edit mode
  // Wait for products to load first so dropdowns can show selected values
  useEffect(() => {
    if (!isOpen || !editMode || !existingOrderData) return;
    if (productsLoading) return;

    setCustomer(existingOrderData.customer);

    const initializedItems = existingOrderData.items.map((item) => {
      if (item.productId && products.some((p) => p.id === item.productId)) {
        return item;
      }
      if (!item.productId && item.name) {
        const matchedProduct = products.find(
          (p) => p.name.toLowerCase() === item.name.toLowerCase()
        );
        if (matchedProduct) {
          return { ...item, productId: matchedProduct.id };
        }
      }
      return {
        ...item,
        productId: CUSTOM_LINE_ITEM_VALUE,
        unitPriceText: Number.isFinite(item.unitPrice) ? String(item.unitPrice) : ''
      };
    });
    setItems(initializedItems);
    
    setDeliveryOption(existingOrderData.deliveryOption);
    setDeliveryDate(
      existingOrderData.deliveryDate
        ? existingOrderData.deliveryDate.toISOString().split('T')[0]
        : ''
    );
    setDeliveryInstructions(existingOrderData.deliveryInstructions || '');
    setPaymentMethod(existingOrderData.paymentMethod);
    setPaymentId(existingOrderData.paymentId || '');
    setShippingAmount(existingOrderData.shippingAmount !== undefined ? String(existingOrderData.shippingAmount) : '');
    setDiscountType(existingOrderData.adminDiscountType || '');
    setDiscountValue(existingOrderData.adminDiscountValue !== undefined ? String(existingOrderData.adminDiscountValue) : '');
    setAdminNotes(existingOrderData.adminNotes || '');
  }, [isOpen, editMode, existingOrderData, productsLoading, products]);

  // T8: keep unitPrice in sync with product + selected customisations.
  useEffect(() => {
    if (productsLoading) return;
    if (!products.length) return;

    let didChange = false;
    const nextItems = items.map((line) => {
      if (!line.productId || isCustomLineItem(line)) return line;
      const product = products.find((p) => p.id === line.productId);
      if (!product) return line;

      const computed = computeUnitPrice(product, line, colorPricingSettings);
      const current = Number.isFinite(line.unitPrice) ? line.unitPrice : 0;
      if (Math.abs(computed - current) > 0.001) {
        didChange = true;
        return { ...line, unitPrice: computed };
      }

      return line;
    });

    if (didChange) setItems(nextItems);
  }, [items, products, productsLoading, colorPricingSettings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!customer.email?.trim()) {
      setError('Customer email is required.');
      return;
    }
    if (!customer.firstName?.trim() || !customer.lastName?.trim()) {
      setError('Customer first and last name are required.');
      return;
    }

    if (discountType && discountValue.trim() === '') {
      setError('Discount value is required when a discount type is selected.');
      return;
    }

    if (discountType) {
      const parsed = parseFloat(discountValue);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setError('Discount value must be a valid number >= 0.');
        return;
      }
      if (discountType === 'percentage' && parsed > 100) {
        setError('Discount percentage cannot be greater than 100.');
        return;
      }
    }
    if (
      deliveryOption === 'delivery' &&
      !customer.address?.line1?.trim()
    ) {
      setError('For delivery, address is required.');
      return;
    }
    const resolvedLineItems: { line: CreateOrderLineDraft; unitPrice: number }[] = [];

    for (let i = 0; i < items.length; i++) {
      const line = items[i];

      if (isCustomLineItem(line)) {
        if (!line.name.trim()) {
          setError(`Line item ${i + 1}: Enter a product name.`);
          return;
        }
        if (!(line.unitPrice >= 0) || !Number.isFinite(line.unitPrice) || !(line.quantity > 0)) {
          setError(`Line item ${i + 1}: Invalid quantity or price.`);
          return;
        }
        resolvedLineItems.push({
          line: { ...line, productId: undefined },
          unitPrice: line.unitPrice
        });
        continue;
      }

      if (!line.productId) {
        setError(`Line item ${i + 1}: Select a product or Custom item.`);
        return;
      }

      const product = products.find((p) => p.id === line.productId);
      if (!product) {
        setError(`Line item ${i + 1}: Selected product not found.`);
        return;
      }

      const computedUnitPrice = computeUnitPrice(product, line, colorPricingSettings);
      if (!(computedUnitPrice >= 0) || !(line.quantity > 0)) {
        setError(`Line item ${i + 1}: Invalid quantity or price.`);
        return;
      }

      const requireFlavors = (product.flavors || []).length > 0;
      if (requireFlavors && (!line.flavors || line.flavors.length === 0)) {
        setError(`Line item ${i + 1} (${product.name}): Please select at least one flavor.`);
        return;
      }

      const requireBaseColor = product.enableBaseColor !== false;
      if (requireBaseColor && !line.baseColor) {
        setError(`Line item ${i + 1} (${product.name}): Please select a base color.`);
        return;
      }

      const requirePipingColor = product.enablePipingColor !== false;
      if (requirePipingColor && !line.pipingColor) {
        setError(`Line item ${i + 1} (${product.name}): Please select a piping color.`);
        return;
      }

      const requireSize = (product.sizes || []).length > 0;
      if (requireSize && !line.size) {
        setError(`Line item ${i + 1} (${product.name}): Please select a size.`);
        return;
      }

      const requireShape = (product.availableShapes || []).length > 0;
      if (requireShape && !line.shape) {
        setError(`Line item ${i + 1} (${product.name}): Please select a shape.`);
        return;
      }

      const requireFilling = (product.fillings || []).length > 0;
      if (requireFilling && !line.filling) {
        setError(`Line item ${i + 1} (${product.name}): Please select a filling.`);
        return;
      }

      const requireIcing = (product.coverIcingOptions || []).length > 0;
      if (requireIcing && !line.icing) {
        setError(`Line item ${i + 1} (${product.name}): Please select an icing.`);
        return;
      }

      resolvedLineItems.push({ line, unitPrice: computedUnitPrice });
    }

    if (resolvedLineItems.length === 0) {
      setError('Add at least one valid line item.');
      return;
    }

    setSubmitting(true);
    try {
      if (editMode && existingOrderId) {
        // Helper function to remove undefined values from objects
        const removeUndefined = (obj: any): any => {
          if (Array.isArray(obj)) {
            return obj.map(item => removeUndefined(item));
          }
          if (obj && typeof obj === 'object' && !(obj instanceof Date)) {
            return Object.entries(obj).reduce((acc, [key, value]) => {
              if (value !== undefined) {
                acc[key] = removeUndefined(value);
              }
              return acc;
            }, {} as any);
          }
          return obj;
        };

        // Get the existing order to preserve all OrderItem fields
        const existingOrder = await orderService.getOrder(existingOrderId);
        if (!existingOrder) {
          throw new Error('Order not found');
        }

        // Build updated items by preserving existing OrderItem fields
        // and only updating the customization fields
        const updatedItems = resolvedLineItems.map(({ line, unitPrice }, index) => {
          // Try to match with existing item (by index for now since we're editing in place)
          const existingItem = existingOrder.items[index];
          
          // Calculate new prices
          const totalPrice = unitPrice * line.quantity;
          
          if (existingItem) {
            // Update existing item, preserving all OrderItem fields
            return {
              ...existingItem,
              productId: line.productId || '',
              name: line.name.trim(),
              quantity: line.quantity,
              unitPrice,
              totalPrice,
              size: line.size?.trim() || '',
              shape: line.shape?.trim() || '',
              layers: (line.layers?.trim() || line.size?.trim()) || '',
              flavors: line.flavors?.length ? line.flavors : [],
              filling: line.filling?.trim() || '',
              icing: line.icing?.trim() || '',
              baseColor: line.baseColor?.trim() || '',
              pipingColor: line.pipingColor?.trim() || '',
              specialInstructions: line.specialInstructions?.trim() || '',
              addOns: line.addOns?.length ? line.addOns : [],
              matchingCupcakes: line.matchingCupcakes?.length ? line.matchingCupcakes : [],
              updatedAt: new Date()
            };
          } else {
            // New item - need to create full OrderItem structure
            return {
              id: `temp-${Date.now()}-${index}`,
              productId: line.productId || '',
              orderId: existingOrderId,
              status: existingOrder.status,
              name: line.name.trim(),
              imageUrl: existingOrder.items[0]?.imageUrl || '/placeholder-cake.jpg',
              size: line.size?.trim() || '',
              flavors: line.flavors?.length ? line.flavors : [],
              shape: line.shape?.trim() || '',
              layers: (line.layers?.trim() || line.size?.trim()) || '',
              filling: line.filling?.trim() || '',
              icing: line.icing?.trim() || '',
              baseColor: line.baseColor?.trim() || '',
              pipingColor: line.pipingColor?.trim() || '',
              specialInstructions: line.specialInstructions?.trim() || '',
              addOns: line.addOns?.length ? line.addOns : [],
              matchingCupcakes: line.matchingCupcakes?.length ? line.matchingCupcakes : [],
              hasImageOnCake: false,
              hasSampleImage: false,
              deliveryOption: existingOrder.deliveryOption,
              deliveryDate: existingOrder.deliveryDate,
              quantity: line.quantity,
              basePrice: unitPrice,
              flavorAdditionalPrice: 0,
              fillingAdditionalPrice: 0,
              icingAdditionalPrice: 0,
              addOnsAdditionalPrice: 0,
              matchingCupcakesAdditionalPrice: 0,
              imageOnCakeAdditionalPrice: 0,
              baseColorAdditionalPrice: 0,
              unitPrice,
              totalPrice,
              addedAt: new Date(),
              updatedAt: new Date()
            };
          }
        });

        // Update existing order
        const updateData: any = {
          items: updatedItems,
          deliveryOption
        };

        // Add optional fields only if they have values
        if (deliveryDate) {
          updateData.deliveryDate = new Date(deliveryDate);
        }
        if (deliveryOption === 'delivery' && customer.address?.line1?.trim()) {
          updateData.deliveryAddress = customer.address;
        }
        if (deliveryInstructions.trim()) {
          updateData.deliveryInstructions = deliveryInstructions.trim();
        }
        if (adminNotes.trim()) {
          updateData.notes = adminNotes.trim();
        }

        // Calculate new subtotal
        const newSubtotal = updatedItems.reduce(
          (sum, item) => sum + item.totalPrice,
          0
        );

        const newVat = newSubtotal * (existingOrder.vatPercentage / 100);
        const shipping = shippingAmount !== '' ? parseFloat(shippingAmount) : existingOrder.shipping;
        const subtotalPlusVatAndShipping = newSubtotal + newVat + shipping;

        // Calculate new discount
        let newDiscountAmount = 0;
        if (discountType && discountValue) {
          const parsedDiscount = parseFloat(discountValue);
          if (discountType === 'percentage') {
            newDiscountAmount = (subtotalPlusVatAndShipping * parsedDiscount) / 100;
          } else if (discountType === 'fixed') {
            newDiscountAmount = parsedDiscount;
          }
        } else if (!discountType) {
          newDiscountAmount = 0;
        }

        const newTotal = Math.max(0, subtotalPlusVatAndShipping - newDiscountAmount);

        updateData.subtotal = newSubtotal;
        updateData.vat = newVat;
        updateData.shipping = shipping;
        updateData.total = newTotal;
        updateData.originalTotal = subtotalPlusVatAndShipping;
        updateData.couponDiscount = newDiscountAmount;
        
        if (discountType) {
          updateData.coupon = {
            ...(existingOrder.coupon || {}),
            discountType,
            discountValue: parseFloat(discountValue)
          };
        } else {
          // If discount was removed, we should ideally remove the coupon object, but Firestore update won't delete it unless we use deleteField(). 
          // For now, setting couponDiscount to 0 is enough to fix the total.
          updateData.coupon = null;
        }

        // Remove any undefined values before sending to Firestore
        const cleanedUpdateData = removeUndefined(updateData);

        await orderService.updateOrder(existingOrderId, cleanedUpdateData);
      } else {
        // Create new order
        const data: ManualOrderCreateData = {
          customer: {
            ...customer,
            email: customer.email.trim(),
            firstName: customer.firstName.trim(),
            lastName: customer.lastName.trim(),
            phone: customer.phone?.trim() || undefined,
            address:
              deliveryOption === 'delivery' && customer.address
                ? {
                    line1: customer.address.line1?.trim() || '',
                    line2: undefined,
                    city: '',
                    state: undefined,
                    postalCode: customer.address.postalCode?.trim() || '',
                    country: 'Ireland'
                  }
                : undefined,
            deliveryOption,
            deliveryInstructions: deliveryInstructions.trim() || undefined
          },
          items: resolvedLineItems.map(({ line, unitPrice }) => ({
            productId: line.productId || undefined,
            name: line.name.trim(),
            quantity: line.quantity,
            unitPrice,
            size: line.size?.trim(),
            shape: line.shape?.trim(),
            layers: (line.layers?.trim() || line.size?.trim()) as string,
            flavors: line.flavors?.length ? line.flavors : undefined,
            filling: line.filling?.trim(),
            icing: line.icing?.trim(),
            baseColor: line.baseColor?.trim(),
            pipingColor: line.pipingColor?.trim(),
            specialInstructions: line.specialInstructions?.trim(),
            addOns: line.addOns?.length ? line.addOns : undefined,
            matchingCupcakes: line.matchingCupcakes?.length ? line.matchingCupcakes : undefined,
            description: line.description?.trim()
          })),
          deliveryOption,
          deliveryDate: deliveryDate ? new Date(deliveryDate) : undefined,
          deliveryAddress:
            deliveryOption === 'delivery' &&
            customer.address?.line1?.trim()
              ? customer.address
              : undefined,
          deliveryInstructions: deliveryInstructions.trim() || undefined,
          paymentMethod,
          paymentId: paymentId.trim() || undefined,
          shippingAmount: shippingAmount !== '' ? parseFloat(shippingAmount) : undefined,
          adminDiscountType: discountType || undefined,
          adminDiscountValue: discountType ? parseFloat(discountValue) : undefined,
          adminNotes: adminNotes.trim() || undefined
        };

        await orderService.createManualOrder(data);
      }
      
      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error(editMode ? 'Update order failed:' : 'Create manual order failed:', err);
      setError(err instanceof Error ? err.message : `Failed to ${editMode ? 'update' : 'create'} order.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-order-modal-overlay" onClick={handleClose}>
      <div
        className="create-order-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="create-order-title"
      >
        <div className="create-order-modal-header">
          <h2 id="create-order-title">{editMode ? 'Edit order' : 'Create order manually'}</h2>
          <button
            type="button"
            className="create-order-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="create-order-form">
          <div className="create-order-modal-body">
            {error && (
              <div className="create-order-error" role="alert">
                {error}
              </div>
            )}

            <section className="create-order-section">
              <h3>Customer</h3>
              <div className="create-order-fields create-order-fields-grid">
                <FormField
                  id="customer-firstName"
                  label="First name"
                  type="text"
                  value={customer.firstName}
                  onChange={(e) => updateCustomer('firstName', e.target.value)}
                  required
                />
                <FormField
                  id="customer-lastName"
                  label="Last name"
                  type="text"
                  value={customer.lastName}
                  onChange={(e) => updateCustomer('lastName', e.target.value)}
                  required
                />
                <FormField
                  id="customer-email"
                  label="Email"
                  type="email"
                  value={customer.email}
                  onChange={(e) => updateCustomer('email', e.target.value)}
                  required
                />
                <FormField
                  id="customer-phone"
                  label="Phone"
                  type="tel"
                  value={customer.phone || ''}
                  onChange={(e) => updateCustomer('phone', e.target.value)}
                />
              </div>
            </section>

            <section className="create-order-section">
              <h3>Line items</h3>
              <p className="create-order-section-hint">
                Select a catalog product, or choose Custom item to type a name and price.
              </p>
              {productsError && (
                <div className="create-order-error" role="alert">
                  {productsError}
                </div>
              )}
              <div className="create-order-items">
                {items.map((item, index) => {
                  const customItem = isCustomLineItem(item);
                  return (
                  <div key={index} className="create-order-item-row">
                    <div className="create-order-item-cols">
                      <FormField
                        id={`item-product-${index}`}
                        label="Product"
                        type="select"
                        value={item.productId || ''}
                        onChange={(e) => {
                          const selectedProductId = e.target.value;

                          if (selectedProductId === CUSTOM_LINE_ITEM_VALUE) {
                            setItem(index, {
                              productId: CUSTOM_LINE_ITEM_VALUE,
                              unitPriceText:
                                item.unitPriceText ??
                                (Number.isFinite(item.unitPrice) ? String(item.unitPrice) : '')
                            });
                            return;
                          }

                          const product = products.find((p) => p.id === selectedProductId);

                          if (!product) {
                            setItem(index, {
                              productId: selectedProductId,
                              name: '',
                              unitPrice: 0,
                              unitPriceText: ''
                            });
                            return;
                          }

                          const firstAvailableFlavor =
                            product.flavors?.find((f) => f.available)?.name ??
                            product.flavors?.[0]?.name ??
                            '';

                          const firstSize = product.sizes?.[0];
                          const firstSizeName = firstSize?.name ?? '';
                          const firstShape = product.availableShapes?.[0] ?? '';
                          const defaultTierQuantity = firstSize?.quantityTiers?.[0]?.quantity;

                          setItem(index, {
                            productId: selectedProductId,
                            name: product.name,
                            unitPrice: 0,
                            unitPriceText: '',
                            size: product.sizes?.length ? firstSizeName : '',
                            layers: product.sizes?.length ? firstSizeName : '',
                            shape: product.availableShapes?.length ? firstShape : '',
                            flavors: product.flavors?.length && firstAvailableFlavor ? [firstAvailableFlavor] : [],
                            filling: product.fillings?.length ? product.fillings[0] : '',
                            icing: product.coverIcingOptions?.length ? product.coverIcingOptions[0] : '',
                            quantity: defaultTierQuantity ?? 1,
                            addOns: [],
                            matchingCupcakes: [],
                            baseColor: product.enableBaseColor !== false ? AVAILABLE_COLORS[0].name : '',
                            pipingColor: product.enablePipingColor !== false ? AVAILABLE_COLORS[0].name : '',
                            specialInstructions: '',
                            description: ''
                          });
                        }}
                        placeholder={productsLoading ? 'Loading products…' : 'Select a product or custom item'}
                        options={[
                          { value: CUSTOM_LINE_ITEM_VALUE, label: 'Custom item' },
                          ...products.map((p) => ({ value: p.id, label: p.name }))
                        ]}
                      />
                      {(() => {
                        const product = products.find((p) => p.id === item.productId);
                        const selectedSize =
                          (product?.sizes || []).find((s) => s.name === item.size) || (product?.sizes || [])[0];
                        const quantityTiers = selectedSize?.quantityTiers || [];

                        if (!customItem && quantityTiers.length > 0) {
                          return (
                            <FormField
                              id={`item-qty-${index}`}
                              label="Qty"
                              type="select"
                              value={String(item.quantity)}
                              onChange={(e) =>
                                setItem(index, {
                                  quantity: parseInt(e.target.value, 10) || 0
                                })
                              }
                              options={quantityTiers.map((t) => ({
                                value: String(t.quantity),
                                label: String(t.quantity)
                              }))}
                            />
                          );
                        }

                        return (
                          <FormField
                            id={`item-qty-${index}`}
                            label="Qty"
                            type="number"
                            value={String(item.quantity)}
                            onChange={(e) =>
                              setItem(index, {
                                quantity: parseInt(e.target.value, 10) || 0
                              })
                            }
                            min={1}
                          />
                        );
                      })()}
                      <div className="create-order-item-price-and-remove">
                        {customItem ? (
                          <FormField
                            id={`item-price-${index}`}
                            label="Unit price (€)"
                            type="number"
                            value={item.unitPriceText ?? (Number.isFinite(item.unitPrice) ? String(item.unitPrice) : '')}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const parsed = parseFloat(raw);
                              setItem(index, {
                                unitPriceText: raw,
                                unitPrice: Number.isFinite(parsed) ? parsed : 0
                              });
                            }}
                            min={0}
                            step={0.01}
                            placeholder="0.00"
                          />
                        ) : (
                          <div className="create-order-unit-price-display">
                            Unit price: €{Number.isFinite(item.unitPrice) ? item.unitPrice.toFixed(2) : '0.00'}
                          </div>
                        )}
                        <button
                          type="button"
                          className="create-order-remove-item"
                          onClick={() => removeLineItem(index)}
                          disabled={items.length === 1}
                          aria-label="Remove line"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    {customItem ? (
                      <FormField
                        id={`item-name-${index}`}
                        label="Item name"
                        type="text"
                        value={item.name}
                        onChange={(e) => setItem(index, { name: e.target.value })}
                        placeholder="e.g. Custom 2-tier wedding cake"
                        required
                      />
                    ) : null}
                    <div className="create-order-item-customizations">
                      <button
                        type="button"
                        className="create-order-item-customizations-toggle"
                        onClick={() => toggleItemExpanded(index)}
                      >
                        <h4 className="create-order-item-customizations-title">Customization</h4>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            transform: expandedItems.has(index) ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        >
                          <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      
                      {expandedItems.has(index) && (() => {
                        if (customItem) {
                          return (
                            <div className="create-order-customization-grid">
                              <FormField
                                id={`item-size-${index}`}
                                label="Size"
                                type="text"
                                value={item.size || ''}
                                onChange={(e) => setItem(index, { size: e.target.value, layers: e.target.value })}
                                placeholder="Optional"
                              />
                              <FormField
                                id={`item-shape-${index}`}
                                label="Shape"
                                type="text"
                                value={item.shape || ''}
                                onChange={(e) => setItem(index, { shape: e.target.value })}
                                placeholder="Optional"
                              />
                              <FormField
                                id={`item-layers-${index}`}
                                label="Layers"
                                type="text"
                                value={item.layers || ''}
                                onChange={(e) => setItem(index, { layers: e.target.value })}
                                placeholder="Optional"
                              />
                              <FormField
                                id={`item-flavors-${index}`}
                                label="Flavors"
                                type="text"
                                value={(item.flavors || []).join(', ')}
                                onChange={(e) =>
                                  setItem(index, {
                                    flavors: e.target.value
                                      .split(',')
                                      .map((flavor) => flavor.trim())
                                      .filter(Boolean)
                                  })
                                }
                                placeholder="Comma-separated"
                              />
                              <FormField
                                id={`item-filling-${index}`}
                                label="Filling"
                                type="text"
                                value={item.filling || ''}
                                onChange={(e) => setItem(index, { filling: e.target.value })}
                                placeholder="Optional"
                              />
                              <FormField
                                id={`item-icing-${index}`}
                                label="Icing"
                                type="text"
                                value={item.icing || ''}
                                onChange={(e) => setItem(index, { icing: e.target.value })}
                                placeholder="Optional"
                              />
                              <FormField
                                id={`item-baseColor-${index}`}
                                label="Base color"
                                type="text"
                                value={item.baseColor || ''}
                                onChange={(e) => setItem(index, { baseColor: e.target.value })}
                                placeholder="Optional"
                              />
                              <FormField
                                id={`item-pipingColor-${index}`}
                                label="Piping color"
                                type="text"
                                value={item.pipingColor || ''}
                                onChange={(e) => setItem(index, { pipingColor: e.target.value })}
                                placeholder="Optional"
                              />
                              <div className="create-order-customization-full">
                                <FormField
                                  id={`item-specialInstructions-${index}`}
                                  label="Message on cake"
                                  type="textarea"
                                  value={item.specialInstructions || ''}
                                  onChange={(e) =>
                                    setItem(index, { specialInstructions: e.target.value })
                                  }
                                  placeholder="Optional"
                                />
                              </div>
                              <div className="create-order-customization-full">
                                <FormField
                                  id={`item-description-${index}`}
                                  label="Notes"
                                  type="textarea"
                                  value={item.description || ''}
                                  onChange={(e) => setItem(index, { description: e.target.value })}
                                  placeholder="Optional extra details"
                                />
                              </div>
                            </div>
                          );
                        }

                        const product = products.find((p) => p.id === item.productId);
                        if (!product) return null;

                        return (
                          <div className="create-order-customization-grid">
                            {product.sizes && product.sizes.length > 0 && (
                              <FormField
                                id={`item-size-${index}`}
                                label="Size"
                                type="select"
                                value={item.size || ''}
                                onChange={(e) => {
                                  const sizeName = e.target.value;
                                  const selectedSize = product.sizes.find((s) => s.name === sizeName);
                                  const defaultTierQuantity = selectedSize?.quantityTiers?.[0]?.quantity;

                                  setItem(index, {
                                    size: sizeName,
                                    layers: sizeName,
                                    quantity: defaultTierQuantity ?? item.quantity
                                  });
                                }}
                                options={product.sizes.map((s) => ({
                                  value: s.name,
                                  label: `${s.name} (${s.price}€)`
                                }))}
                              />
                            )}
                            
                            {product.availableShapes && product.availableShapes.length > 0 && (
                              <FormField
                                id={`item-shape-${index}`}
                                label="Shape"
                                type="select"
                                value={item.shape || ''}
                                onChange={(e) => setItem(index, { shape: e.target.value })}
                                options={product.availableShapes.map((shape) => ({
                                  value: shape,
                                  label: shape
                                }))}
                              />
                            )}

                            {product.sizes && product.sizes.length > 0 && (
                              <FormField
                                id={`item-layers-${index}`}
                                label="Layers (auto)"
                                type="text"
                                value={item.layers || ''}
                                onChange={(e) => {
                                  void e;
                                }}
                                disabled
                              />
                            )}
                        <div className="create-order-customization-full">
                          <div className="create-order-option-group">
                            <div className="create-order-option-group-label">
                              Flavors (choose up to{' '}
                              {(products.find((p) => p.id === item.productId)?.maxFlavorsSelectable ??
                                1) as number}
                              )
                            </div>
                            <div className="create-order-option-checkbox-grid">
                              {(products.find((p) => p.id === item.productId)?.flavors || [])
                                .filter((f) => f.available)
                                .map((flavor) => {
                                  const selectedFlavors = item.flavors || [];
                                  const isSelected = selectedFlavors.includes(flavor.name);
                                  return (
                                    <label
                                      key={flavor.name}
                                      className="create-order-option-checkbox"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {
                                          if (isSelected) {
                                            setItem(index, {
                                              flavors: selectedFlavors.filter((f) => f !== flavor.name)
                                            });
                                            return;
                                          }

                                          const maxFlavors =
                                            (products.find((p) => p.id === item.productId)?.maxFlavorsSelectable ??
                                              1) as number;

                                          if (selectedFlavors.length >= maxFlavors) {
                                            // Replace the first selection when max is reached.
                                            setItem(index, {
                                              flavors: [...selectedFlavors.slice(1), flavor.name]
                                            });
                                          } else {
                                            setItem(index, { flavors: [...selectedFlavors, flavor.name] });
                                          }
                                        }}
                                      />
                                      <span className="create-order-option-checkbox-label">
                                        {flavor.name}
                                        {flavor.price !== undefined
                                          ? ` +€${flavor.price.toFixed(2)}`
                                          : ''}
                                      </span>
                                    </label>
                                  );
                                })}
                            </div>
                          </div>
                        </div>
                        <FormField
                          id={`item-filling-${index}`}
                          label="Filling"
                          type="select"
                          value={item.filling || ''}
                          onChange={(e) => setItem(index, { filling: e.target.value })}
                          options={(products.find((p) => p.id === item.productId)?.fillings || []).map((f) => ({
                            value: f,
                            label: f
                          }))}
                        />
                        <FormField
                          id={`item-icing-${index}`}
                          label="Icing"
                          type="select"
                          value={item.icing || ''}
                          onChange={(e) => setItem(index, { icing: e.target.value })}
                          options={(products.find((p) => p.id === item.productId)?.coverIcingOptions || []).map((i) => ({
                            value: i,
                            label: i
                          }))}
                        />
                        {products.find((p) => p.id === item.productId)?.enableBaseColor !== false && (
                          <FormField
                            id={`item-baseColor-${index}`}
                            label="Base color"
                            type="select"
                            value={item.baseColor || ''}
                            onChange={(e) => setItem(index, { baseColor: e.target.value })}
                            options={AVAILABLE_COLORS.map((c) => {
                              const selectedProduct = products.find((p) => p.id === item.productId);
                              const colorPrice = getBaseColorAdditionalPrice(c.name, {
                                collection: selectedProduct?.collection,
                                productId: selectedProduct?.id,
                                settings: colorPricingSettings
                              });
                              return {
                                value: c.name,
                                label: colorPrice > 0
                                  ? `${c.name} (+€${colorPrice.toFixed(2)})`
                                  : c.name
                              };
                            })}
                          />
                        )}

                        {products.find((p) => p.id === item.productId)?.enablePipingColor !== false && (
                          <div className="create-order-option-group">
                            <div className="create-order-option-group-label">
                              Piping color (choose up to 2)
                            </div>
                            <div className="create-order-option-checkbox-grid">
                              {AVAILABLE_COLORS.map((color) => {
                                const selectedPipingColors = item.pipingColor
                                  ? item.pipingColor
                                      .split(',')
                                      .map((x) => x.trim())
                                      .filter(Boolean)
                                  : [];
                                const isSelected = selectedPipingColors.includes(color.name);
                                return (
                                  <label
                                    key={color.name}
                                    className="create-order-option-checkbox"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => {
                                        if (isSelected) {
                                          setItem(index, {
                                            pipingColor: selectedPipingColors
                                              .filter((c) => c !== color.name)
                                              .join(', ')
                                          });
                                          return;
                                        }

                                        if (selectedPipingColors.length >= 2) {
                                          setItem(index, {
                                            pipingColor: [
                                              ...selectedPipingColors.slice(1),
                                              color.name
                                            ].join(', ')
                                          });
                                        } else {
                                          setItem(index, {
                                            pipingColor: [...selectedPipingColors, color.name].join(', ')
                                          });
                                        }
                                      }}
                                    />
                                    <span className="create-order-option-checkbox-label">
                                      {color.name}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                            {product.enableMessageOnCake !== false && (
                              <div className="create-order-customization-full">
                                <FormField
                                  id={`item-specialInstructions-${index}`}
                                  label="Message on cake"
                                  type="textarea"
                                  value={item.specialInstructions || ''}
                                  onChange={(e) =>
                                    setItem(index, { specialInstructions: e.target.value })
                                  }
                                  placeholder="Text or design to go on the cake"
                                />
                              </div>
                            )}

                            {product.addOns && product.addOns.length > 0 && (
                              <div className="create-order-customization-full">
                                <div className="create-order-option-group">
                                  <div className="create-order-option-group-label">
                                    Add-ons
                                  </div>
                                  <div className="create-order-option-checkbox-grid">
                                    {product.addOns.map(
                                      (addOn) => {
                                        const selectedAddOns = item.addOns || [];
                                        const isSelected = selectedAddOns.includes(addOn);
                                        return (
                                          <label
                                            key={addOn}
                                            className="create-order-option-checkbox"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              onChange={() => {
                                                if (isSelected) {
                                                  setItem(index, {
                                                    addOns: selectedAddOns.filter(
                                                      (a) => a !== addOn
                                                    )
                                                  });
                                                  return;
                                                }
                                                setItem(index, { addOns: [...selectedAddOns, addOn] });
                                              }}
                                            />
                                            <span className="create-order-option-checkbox-label">
                                              {addOn}
                                            </span>
                                          </label>
                                        );
                                      }
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {product.matchingCupcakes && product.matchingCupcakes.length > 0 && (
                              <div className="create-order-customization-full">
                                <div className="form-field">
                                  <label
                                    htmlFor={`item-matchingCupcakes-${index}`}
                                    className="form-field-label"
                                  >
                                    Matching cupcakes
                                  </label>
                                  <select
                                    id={`item-matchingCupcakes-${index}`}
                                    className="form-field-input"
                                    value={item.matchingCupcakes?.[0] ?? ''}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setItem(index, { matchingCupcakes: v ? [v] : [] });
                                    }}
                                  >
                                    <option value="">None</option>
                                    {product.matchingCupcakes.map(
                                      (c) => (
                                        <option key={c} value={c}>
                                          {c}
                                        </option>
                                      )
                                    )}
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="create-order-add-item"
                onClick={addLineItem}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3.33337V12.6667M3.33333 8H12.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Add line item
              </button>
              <p className="create-order-subtotal">
                Subtotal: €{subtotal.toFixed(2)} (VAT & shipping applied when order is created)
              </p>
            </section>

            <section className="create-order-section">
              <h3>Delivery</h3>
              <div className="create-order-fields">
                <FormField
                  id="delivery-option"
                  label="Delivery option"
                  type="select"
                  value={deliveryOption}
                  onChange={(e) =>
                    setDeliveryOption(e.target.value as 'collection' | 'delivery')
                  }
                  options={[
                    { value: 'collection', label: 'Collection' },
                    { value: 'delivery', label: 'Delivery' }
                  ]}
                />
                <FormField
                  id="delivery-date"
                  label="Delivery / collection date"
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                />
                <FormField
                  id="delivery-instructions"
                  label="Delivery instructions"
                  type="textarea"
                  value={deliveryInstructions}
                  onChange={(e) => setDeliveryInstructions(e.target.value)}
                  placeholder="Optional"
                />
                {deliveryOption === 'delivery' && (
                  <>
                    <FormField
                      id="address-line1"
                      label="Address"
                      type="text"
                      value={customer.address?.line1 || ''}
                      onChange={(e) => updateAddress('line1', e.target.value)}
                      required
                    />
                    <FormField
                      id="address-postalCode"
                      label="Postal code"
                      type="text"
                      value={customer.address?.postalCode || ''}
                      onChange={(e) => updateAddress('postalCode', e.target.value)}
                    />
                  </>
                )}
              </div>
            </section>

            <section className="create-order-section">
              <h3>Payment</h3>
              <div className="create-order-fields create-order-fields-grid">
                <FormField
                  id="payment-method"
                  label="Payment method"
                  type="select"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                  options={PAYMENT_METHOD_OPTIONS}
                />
                <FormField
                  id="payment-id"
                  label="Payment ID / reference"
                  type="text"
                  value={paymentId}
                  onChange={(e) => setPaymentId(e.target.value)}
                  placeholder="e.g. Stripe pi_xxx, bank ref, CASH"
                />
                <FormField
                  id="shipping-amount"
                  label="Shipping amount (€) — optional"
                  type="number"
                  value={shippingAmount}
                  onChange={(e) => setShippingAmount(e.target.value)}
                  min={0}
                  placeholder="Leave blank to default to €0"
                />
                <div className="form-field">
                  <label htmlFor="discount-type" className="form-field-label">
                    Discount
                  </label>
                  <select
                    id="discount-type"
                    className="form-field-input"
                    value={discountType}
                    onChange={(e) => {
                      const v = e.target.value as CouponDiscountType | '';
                      setDiscountType(v);
                      if (!v) setDiscountValue('');
                    }}
                  >
                    <option value="">None</option>
                    <option value="fixed">Fixed amount (€)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <FormField
                  id="discount-value"
                  label="Discount value"
                  type="number"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  disabled={!discountType}
                  min={0}
                  placeholder={discountType === 'percentage' ? 'e.g. 10' : 'e.g. 5.00'}
                />
              </div>
            </section>

            <section className="create-order-section">
              <h3>Admin notes</h3>
              <FormField
                id="admin-notes"
                label="Admin notes"
                type="textarea"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Optional internal notes"
              />
            </section>
          </div>

          <div className="create-order-modal-footer">
            <button
              type="button"
              className="create-order-cancel-btn"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="create-order-submit-btn"
              disabled={submitting}
            >
              {submitting ? (editMode ? 'Updating…' : 'Creating…') : (editMode ? 'Update order' : 'Create order')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrderModal;
